import { MarketingShell } from '../../components/MarketingShell'
import { Seo } from '../../components/Seo'
import { Faq, faqJsonLd } from '../../components/Faq'
import { SHARED_FAQ } from '../../lib/marketing-faq'
import { ButtonLink } from '../../components/ui/Button'
import { WaitlistCta } from '../../components/marketing/WaitlistForm'
import { useScrollDepth } from '../../lib/useScrollDepth'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import {
  EARLY_ADOPTER_CAP,
  EARLY_ADOPTER_DISCOUNT_PERCENT,
  EARLY_ADOPTER_MONTHS,
  PLAN_OPTIONS,
} from '../../lib/plans'

// Plans differ by volume -- only volume (accounts, messages) and
// the number of simultaneous accounting connections differ (pricing signed
// off 2026-10-11). Numbers come from PLAN_OPTIONS so they cannot drift.
const COMMON_FEATURES = [
  'WhatsApp & SMS reminders on a reminder schedule you control',
  'Checks your ledger before every message and stops when an invoice is paid',
  'Delivery, read and response tracking for every message',
  'Exportable message history per customer, kept as your record',
  'CSV/Excel import or Xero / QuickBooks sync (Sage coming soon)',
]

const CONNECTIONS_INCLUDED: Record<string, number> = { starter: 1, business: 2, scale: 5 }

const faqItems = [
  {
    question: 'What is included on every plan?',
    answer:
      'The full reminder pipeline: WhatsApp and SMS reminders, rules you control, a ledger check before every send, delivery/read/response tracking, and an exportable message history for every customer. Plans differ only in volume (active customer accounts and messages per month) and how many accounting systems you can connect at once.',
  },
  {
    question: 'How many accounting systems can I connect?',
    answer:
      'Starter includes one accounting connection, Business two, and Scale five. Xero and QuickBooks are supported today; Sage is coming soon. CSV/Excel import is available on every plan.',
  },
  {
    question: 'How do I get access?',
    answer: 'We are letting people in from the waitlist in small groups. Join the waitlist and we will be in touch. Early adopters get 40% off for 12 months.',
  },
  {
    question: 'What is the early adopter offer?',
    answer: `Our first ${EARLY_ADOPTER_CAP} customers get ${EARLY_ADOPTER_DISCOUNT_PERCENT}% off any plan for ${EARLY_ADOPTER_MONTHS} months (Starter R${PLAN_OPTIONS[0].earlyAdopterPrice}, Business R${PLAN_OPTIONS[1].earlyAdopterPrice}, Scale R${PLAN_OPTIONS[2].earlyAdopterPrice} per month). After ${EARLY_ADOPTER_MONTHS} months you move to the list price, with 30 days' notice. In return we ask for a short feedback call at 30 and 90 days.`,
  },
  {
    question: 'What counts as an "active customer account"?',
    answer:
      "A customer who has at least one invoice in an open reminder schedule during the billing period — regardless of how many reminders go out, or whether they're reached over WhatsApp or SMS. A customer with three overdue invoices being chased at once still counts once, not three times.",
  },
  {
    question: 'What counts as a message?',
    answer: `Each plan includes a monthly bundle of WhatsApp and SMS messages, pooled across both channels (${PLAN_OPTIONS[0].includedMessages} / ${PLAN_OPTIONS[1].includedMessages} / ${PLAN_OPTIONS[2].includedMessages.toLocaleString('en-US')}). One message is one WhatsApp or SMS we send to a customer. The app shows you how much of the bundle you have used.`,
  },
  {
    question: "What happens if I go over my plan's active customer accounts?",
    answer: `Each plan includes a set number of active customer accounts per billing period. Beyond that, additional accounts are R${PLAN_OPTIONS[0].overageRate} (Starter), R${PLAN_OPTIONS[1].overageRate} (Business) or R${PLAN_OPTIONS[2].overageRate} (Scale) each for that period. We warn you as you approach the limit and suggest the plan that fits. Scale is capped at 2,000 active customer accounts; beyond that we move you to a custom Enterprise plan.`,
  },
  {
    question: 'Is VAT added to these prices?',
    answer: 'No. The price shown is the price you pay.',
  },
  {
    question: 'What happens if I outgrow my plan?',
    answer:
      'You can move up a tier at any time — your rules, connections, and reminder history carry over unchanged.',
  },
  ...SHARED_FAQ,
]

// See Home.tsx's matching comment for why these are exported.
export const seoTitle = 'Pricing — Plans for Automated Invoice Reminders'
export const seoDescription =
  'RemindLoop pricing: Starter R149/mo (25 active customer accounts, 100 messages), Business R299/mo (100 accounts, 400 messages), Scale R699/mo (500 accounts, 1,500 messages). Early access: 40% off for 12 months for the first 25 customers.'
export const seoJsonLd = faqJsonLd(faqItems)

export default function Pricing() {
  useScrollDepth()
  return (
    <MarketingShell>
      <Seo title={seoTitle} description={seoDescription} path="/pricing" jsonLd={seoJsonLd} />
      <section className="mx-auto max-w-6xl px-[clamp(18px,4vw,44px)] py-[clamp(44px,7vw,88px)]">
        <div className="text-center">
          <h1 className="mx-auto max-w-[20ch] text-[clamp(30px,4.4vw,48px)] leading-[1.1] font-semibold tracking-[-0.03em]">
            Simple, transparent pricing
          </h1>
          <p className="mx-auto mt-4.5 max-w-[58ch] text-[clamp(16px,1.5vw,18px)] leading-[1.6] text-foreground/76">
            Every plan includes the full reminder pipeline — WhatsApp, SMS, and a reminder schedule you
            control — plus an exportable record of every message. Plans differ only in volume: how
            many customers you chase and how many messages you send.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {PLAN_OPTIONS.map((plan) => (
            <Card key={plan.value} className="flex flex-col">
              <CardHeader title={plan.label} />
              <CardBody className="flex flex-1 flex-col justify-between space-y-6 p-6">
                <div>
                  <p className="flex items-baseline gap-1">
                    <span className="text-h3 text-foreground">R{plan.price}</span>
                    <span className="text-body text-foreground/69">/month</span>
                  </p>
                  <p className="mt-1 text-body text-foreground/76">{plan.hint}</p>
                  <p className="mt-1 text-caption text-brand-700">
                    Early adopters: R{plan.earlyAdopterPrice}/month for {EARLY_ADOPTER_MONTHS} months
                  </p>
                  <p className="mt-4 text-body text-foreground">
                    <span className="font-semibold">{plan.includedAccounts}</span> active customer
                    accounts included
                  </p>
                  <p className="text-caption text-foreground/69">
                    R{plan.overageRate} per additional account
                  </p>
                  <p className="mt-2 text-body text-foreground">
                    <span className="font-semibold">
                      {plan.includedMessages.toLocaleString('en-US')}
                    </span>{' '}
                    WhatsApp + SMS messages / month
                  </p>
                  <p className="mt-2 text-body text-foreground">
                    <span className="font-semibold">{CONNECTIONS_INCLUDED[plan.value]}</span>{' '}
                    accounting connection{CONNECTIONS_INCLUDED[plan.value] === 1 ? '' : 's'}
                  </p>
                  <ul className="mt-4 space-y-2 text-body">
                    {COMMON_FEATURES.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <span aria-hidden className="mt-0.5 text-brand-700">
                          ✓
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <WaitlistCta
                  to={`/register?plan=${plan.value}`}
                  source="pricing"
                  planInterest={plan.value}
                  className="min-h-11 w-full justify-center bg-magenta-700 text-white hover:bg-magenta-800"
                >
                  Join the waitlist
                </WaitlistCta>
              </CardBody>
            </Card>
          ))}
        </div>

        <p className="mx-auto mt-6 max-w-[50ch] text-center text-body text-foreground/76">
          The price shown is the price you pay — no VAT is added on top. Early access: 40% off for 12 months.
        </p>

        <Card className="mx-auto mt-10 max-w-3xl">
          <CardBody className="flex flex-col items-center gap-2 p-6 text-center">
            <h2 className="text-card-title text-foreground">Enterprise</h2>
            <p className="max-w-[50ch] text-body text-foreground/76">
              Running more than 2,000 active customer accounts? That's serious scale — Scale plan
              customers move to a custom Enterprise plan, with a dedicated WhatsApp
              number of your own instead of the shared pool. Pricing is negotiated directly, not
              self-serve.
            </p>
            <ButtonLink
              to="/contact?reason=enterprise"
              className="mt-2 bg-magenta-700 text-white hover:bg-magenta-800"
            >
              Contact sales
            </ButtonLink>
          </CardBody>
        </Card>

        <p className="mt-8 text-center text-[13.5px] text-foreground/69">
          Need something custom, or want to talk through which plan fits?{' '}
          <a href="mailto:hello@getremindloop.com" className="font-medium text-brand-700 hover:underline">
            Contact us
          </a>
          .
        </p>

        <div className="mx-auto mt-16 max-w-3xl border-t border-border pt-12">
          <Faq items={faqItems} />
        </div>
      </section>
    </MarketingShell>
  )
}
